import { useState, useEffect, useCallback } from 'react';
import { attendanceAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Search, UserCheck, UserX, Clock, LogIn, LogOut, Users, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const PROXY_ATTENDANCE_ROLES = ['front_desk', 'reception', 'it_admin', 'hospital_admin'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getInitials = (first, last) => `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
const getAvatarColor = (name) => {
  const colors = ['#0D9488', '#2563EB', '#7C3AED', '#DC2626', '#D97706', '#059669', '#4F46E5', '#BE185D', '#0891B2', '#65A30D'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const AttendancePage = () => {
  const { user } = useAuth();
  const canProxyMark = !!user && (user.is_super_admin || PROXY_ATTENDANCE_ROLES.includes(user.role_code));
  const [searchQuery, setSearchQuery] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [marking, setMarking] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const loadDashboard = useCallback(async () => {
    try { const res = await attendanceAPI.getDashboard(); setDashboard(res.data); } catch { /* ignore */ }
  }, []);

  const searchStaff = useCallback(async (q) => {
    setLoading(true);
    try { const res = await attendanceAPI.staffSearch(q); setStaffList(res.data || []); }
    catch { toast.error('Failed to search staff'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); searchStaff(''); }, [loadDashboard, searchStaff]);

  const handleMarkIn = async (staff) => {
    if (!canProxyMark) { toast.error('Only Front Desk / IT Admin / Hospital Admin can mark attendance for others'); return; }
    setMarking(true);
    try {
      await attendanceAPI.proxyMarkIn(staff.id);
      toast.success(`Checked IN: ${staff.first_name} ${staff.last_name}`);
      searchStaff(searchQuery); loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to check in'); }
    setMarking(false);
  };

  const handleMarkOut = async (staff) => {
    if (!canProxyMark) { toast.error('Only Front Desk / IT Admin / Hospital Admin can mark attendance for others'); return; }
    setMarking(true);
    try {
      await attendanceAPI.proxyMarkOut(staff.id);
      toast.success(`Checked OUT: ${staff.first_name} ${staff.last_name}`);
      searchStaff(searchQuery); loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to check out'); }
    setMarking(false);
  };

  const getStatus = (att) => {
    if (!att || att.status === 'not_marked') return 'absent';
    if (att.check_in && att.check_out) return 'completed';
    if (att.check_in) return 'checked_in';
    return 'absent';
  };

  const presentCount = staffList.filter(s => s.today_attendance && s.today_attendance.status === 'present').length;
  const absentCount = staffList.length - presentCount;

  const filteredStaff = staffList.filter(s => {
    if (filterStatus === 'all') return true;
    const st = getStatus(s.today_attendance);
    if (filterStatus === 'present') return st === 'completed' || st === 'checked_in';
    return st === 'absent';
  });

  if (selectedStaff) {
    return <StaffCalendarView staff={selectedStaff} onBack={() => setSelectedStaff(null)} />;
  }

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Staff Attendance</h1>
        <p className="text-sm text-slate-500 mt-0.5">{today}</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users, label: 'Total Staff', value: staffList.length, color: 'blue', tid: 'total-staff' },
          { icon: UserCheck, label: 'Present', value: presentCount, color: 'emerald', tid: 'present-count' },
          { icon: UserX, label: 'Absent', value: absentCount, color: 'red', tid: 'absent-count' },
          { icon: Clock, label: 'Rate', value: `${dashboard?.attendance_rate || 0}%`, color: 'amber', tid: 'att-rate' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-${k.color}-50 flex items-center justify-center`}>
              <k.icon size={18} className={`text-${k.color}-600`} />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase font-medium">{k.label}</p>
              <p className="text-lg font-bold text-slate-900" data-testid={k.tid}>{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by name, phone, email..." value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); searchStaff(e.target.value); }}
              className="pl-10 h-11" data-testid="staff-search-input" />
          </div>
          <div className="flex gap-2">
            {['all', 'present', 'absent'].map(f => (
              <button key={f} onClick={() => setFilterStatus(f)}
                className={cn('px-4 py-2 rounded-full text-xs font-semibold border transition-all capitalize',
                  filterStatus === f
                    ? f === 'present' ? 'bg-emerald-600 text-white border-emerald-600'
                    : f === 'absent' ? 'bg-red-500 text-white border-red-500'
                    : 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                )}
                data-testid={`filter-${f}`}>
                {f === 'present' && <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 mr-1.5" />}
                {f === 'absent' && <span className="inline-block w-2 h-2 rounded-full bg-red-300 mr-1.5" />}
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Staff Cards Grid */}
        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading staff...</div>
        ) : filteredStaff.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No staff found</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredStaff.map(staff => {
              const att = staff.today_attendance;
              const status = getStatus(att);
              const name = `${staff.first_name} ${staff.last_name}`;
              const initials = getInitials(staff.first_name, staff.last_name);
              const color = getAvatarColor(name);

              return (
                <div key={staff.id}
                  className="group bg-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all p-4 cursor-pointer"
                  onClick={() => setSelectedStaff(staff)}
                  data-testid={`staff-card-${staff.id}`}>
                  <div className="flex items-center gap-3.5">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="h-14 w-14 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm"
                        style={{ backgroundColor: color }}>
                        {initials}
                      </div>
                      {/* Status dot */}
                      <div className={cn('absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-white',
                        status === 'completed' || status === 'checked_in' ? 'bg-emerald-500' : 'bg-slate-300'
                      )} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate group-hover:text-teal-700 transition-colors">{name}</p>
                      <p className="text-xs text-slate-500 truncate">{staff.department || 'General'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">{staff.role_name || 'Staff'}</span>
                        {staff.specialization && (
                          <span className="text-[10px] text-slate-400 truncate">{staff.specialization}</span>
                        )}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-1" onClick={e => e.stopPropagation()}>
                      {status === 'absent' && canProxyMark && (
                        <Button size="sm" className="h-8 w-8 p-0 rounded-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleMarkIn(staff)} disabled={marking} data-testid={`mark-in-${staff.id}`} title="Check-In (proxy)">
                          <LogIn size={14} className="text-white" />
                        </Button>
                      )}
                      {status === 'checked_in' && canProxyMark && (
                        <Button size="sm" className="h-8 w-8 p-0 rounded-full bg-red-500 hover:bg-red-600"
                          onClick={() => handleMarkOut(staff)} disabled={marking} data-testid={`mark-out-${staff.id}`} title="Check-Out (proxy)">
                          <LogOut size={14} className="text-white" />
                        </Button>
                      )}
                      {status === 'completed' && (
                        <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center" title="Day complete">
                          <UserCheck size={14} className="text-emerald-600" />
                        </div>
                      )}
                      {!canProxyMark && status === 'absent' && (
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center" title="Not marked">
                          <UserX size={14} className="text-slate-400" />
                        </div>
                      )}
                      {att?.check_in && (
                        <span className="text-[9px] text-slate-400 font-mono">{att.check_in}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ========== Staff Calendar View ========== */
const StaffCalendarView = ({ staff, onBack }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const name = `${staff.first_name} ${staff.last_name}`;
  const initials = getInitials(staff.first_name, staff.last_name);
  const color = getAvatarColor(name);

  useEffect(() => {
    loadMonthly();
  }, [month, year]);

  const loadMonthly = async () => {
    setLoading(true);
    try {
      const res = await attendanceAPI.getUserMonthly(staff.id, { month, year });
      setRecords(res.data || []);
    } catch { toast.error('Failed to load attendance'); }
    setLoading(false);
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayStr = now.toISOString().split('T')[0];
  const todayDate = now.getDate();
  const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();

  const attMap = {};
  records.forEach(r => { attMap[r.date] = r; });

  const presentDays = records.filter(r => r.status === 'present').length;
  const absentDays = (isCurrentMonth ? todayDate : daysInMonth) - presentDays;

  const dayClicked = records.find(r => {
    if (!selectedDay) return false;
    const d = `${year}-${String(month).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    return r.date === d;
  });

  return (
    <div className="space-y-5" data-testid="staff-calendar-view">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="back-btn">
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-base shadow-sm"
            style={{ backgroundColor: color }}>
            {initials}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{name}'s Attendance</h1>
            <p className="text-xs text-slate-500">{staff.department} &middot; {staff.role_name}</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
          <p className="text-2xl font-bold text-emerald-700">{presentDays}</p>
          <p className="text-[10px] text-emerald-600 uppercase font-semibold mt-0.5">Present</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
          <p className="text-2xl font-bold text-red-600">{Math.max(0, absentDays)}</p>
          <p className="text-[10px] text-red-500 uppercase font-semibold mt-0.5">Absent</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
          <p className="text-2xl font-bold text-blue-700">{presentDays > 0 ? Math.round((presentDays / (isCurrentMonth ? todayDate : daysInMonth)) * 100) : 0}%</p>
          <p className="text-[10px] text-blue-600 uppercase font-semibold mt-0.5">Rate</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <button onClick={prevMonth} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid="prev-month">
            <ChevronLeft size={16} className="text-slate-600" />
          </button>
          <h2 className="text-base font-bold text-slate-800" data-testid="current-month">{MONTHS[month - 1]} {year}</h2>
          <button onClick={nextMonth} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid="next-month">
            <ChevronRight size={16} className="text-slate-600" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading...</div>
        ) : (
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(d => (
                <div key={d} className="text-center text-[11px] font-semibold text-slate-400 uppercase py-1">{d}</div>
              ))}
            </div>

            {/* Calendar cells */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells */}
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="h-11" />)}

              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const att = attMap[dateStr];
                const isToday = dateStr === todayStr;
                const isPresent = att?.status === 'present';
                const isPast = new Date(dateStr) < new Date(todayStr);
                const isSelected = selectedDay === day;

                return (
                  <button key={day}
                    onClick={() => setSelectedDay(day)}
                    className={cn(
                      'h-11 rounded-lg flex items-center justify-center text-sm font-medium transition-all relative',
                      isSelected ? 'ring-2 ring-teal-500 ring-offset-1' : '',
                      isPresent ? 'bg-emerald-500 text-white hover:bg-emerald-600' :
                      isPast && !isPresent ? 'bg-red-50 text-red-400 hover:bg-red-100' :
                      isToday ? 'bg-teal-600 text-white hover:bg-teal-700' :
                      'text-slate-700 hover:bg-slate-100'
                    )}
                    data-testid={`day-${day}`}>
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full bg-emerald-500" />
                <span className="text-xs text-slate-500">Present</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full bg-red-200" />
                <span className="text-xs text-slate-500">Absent</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full bg-teal-600" />
                <span className="text-xs text-slate-500">Today</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Selected Day Detail */}
      {selectedDay && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="day-detail">
          <h3 className="text-base font-bold text-teal-700">
            {String(selectedDay).padStart(2, '0')} {MONTHS[month - 1].slice(0, 3)}
            {`${year}-${String(month).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` === todayStr && <span className="text-teal-500 font-medium ml-1">, Today</span>}
          </h3>
          {dayClicked ? (
            <div className="mt-3 space-y-3">
              <span className={cn('inline-block text-xs px-3 py-1 rounded-full font-semibold',
                dayClicked.status === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
              )}>
                {dayClicked.status === 'present' ? 'Present' : 'Absent'}
              </span>
              {dayClicked.check_in && (
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <LogIn size={14} className="text-emerald-500" />
                  <span className="font-mono font-medium">{dayClicked.check_in}</span>
                  <span className="text-slate-400">: Check In</span>
                </div>
              )}
              {dayClicked.check_out && (
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <LogOut size={14} className="text-red-500" />
                  <span className="font-mono font-medium">{dayClicked.check_out}</span>
                  <span className="text-slate-400">: Check Out</span>
                </div>
              )}
              {dayClicked.work_hours > 0 && (
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <Clock size={14} className="text-blue-500" />
                  <span className="font-medium">{dayClicked.work_hours.toFixed(1)} hrs</span>
                  <span className="text-slate-400">worked</span>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-400">No attendance record for this day.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
